import { useState } from "react";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { CampaignTaskGenerationResult } from "../types";

export const GenerateTasksDialog = ({
  definitionId,
  reconcile,
  onClose,
}: {
  definitionId: Identifier;
  reconcile?: boolean;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [fallbackSalesId, setFallbackSalesId] = useState<Identifier | "">("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CampaignTaskGenerationResult | null>(
    null,
  );
  const { data: salespeople } = useGetList("sales", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "first_name", order: "ASC" },
  });

  const run = async () => {
    setLoading(true);
    try {
      const res = reconcile
        ? await (dataProvider as any).reconcileCampaignTasks(
            definitionId,
            fallbackSalesId || null,
          )
        : await (dataProvider as any).generateCampaignTasks(
            definitionId,
            fallbackSalesId || null,
          );
      setResult(res);
      notify("resources.campaigns.generate.success", {
        type: "info",
        messageArgs: res,
      });
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {reconcile
              ? translate("resources.campaigns.generate.reconcile", {
                  _: "Reconcile tasks",
                })
              : translate("resources.campaigns.generate.action", {
                  _: "Generate tasks",
                })}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {translate("resources.campaigns.generate.fallback_help", {
            _: "Select a fallback salesperson for targets without an assigned salesperson.",
          })}
        </p>

        <label className="text-sm font-medium">
          {translate("resources.campaigns.generate.fallback", {
            _: "Fallback salesperson",
          })}
          <select
            className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
            value={fallbackSalesId}
            onChange={(e) =>
              setFallbackSalesId(e.target.value ? Number(e.target.value) : "")
            }
          >
            <option value="">Select salesperson…</option>
            {salespeople?.map((sale) => (
              <option key={sale.id} value={sale.id}>
                {sale.first_name} {sale.last_name}
              </option>
            ))}
          </select>
        </label>

        {result && (
          <div className="text-sm space-y-1 rounded-md border p-3">
            <p>
              {translate("resources.campaigns.generate.created", {
                _: "Created",
              })}
              : {result.created}
            </p>
            <p>
              {translate("resources.campaigns.generate.skipped", {
                _: "Skipped",
              })}
              : {result.skipped}
            </p>
            {result.deleted != null && (
              <p>
                {translate("resources.campaigns.generate.deleted", {
                  _: "Removed",
                })}
                : {result.deleted}
              </p>
            )}
            {result.missing_sales_resolved > 0 && (
              <p className="text-amber-600">
                {translate("resources.campaigns.generate.missing", {
                  _: "Skipped (no salesperson)",
                })}
                : {result.missing_sales_resolved}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {result ? translate("ra.action.close") : translate("ra.action.cancel")}
          </Button>
          {!result && (
            <Button type="button" onClick={run} disabled={loading}>
              {translate("resources.campaigns.generate.confirm", {
                _: "Generate",
              })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
